/**
 * The farmer's review page, in the consent's own language (owner, 2026-09-28: the seven languages the
 * messages go in). The page is the one a farmer reaches from WhatsApp or email; it says only these
 * few things. A language outside the seven is shown in English.
 */
export interface ConsentLinkWords {
  title: string; opening: string; incomplete: string; failed: string; expired: string; invalid: string;
  pdfBefore: string; pdfLink: string; pdfAfter: string;
}

const WORDS: Record<string, ConsentLinkWords> = {
  en: {
    title: 'Your consent record', opening: 'Opening...',
    incomplete: 'That link is not complete. Check the message it came in.',
    failed: 'This link could not be opened just now. Try again in a moment.',
    expired: 'This link has expired. Ask the officer who sent it for a new one.',
    invalid: 'This link is not valid. Check the message it came in, or ask the officer who sent it.',
    pdfBefore: 'This is your consent as it is on record.', pdfLink: 'Open or download the PDF',
    pdfAfter: 'to keep it. If anything here is wrong, tell the officer who sent you this link.',
  },
  hi: {
    title: 'आपकी सहमति का रिकॉर्ड', opening: 'खुल रहा है...',
    incomplete: 'यह लिंक पूरा नहीं है। जिस संदेश में यह आया, उसे जांचें।',
    failed: 'यह लिंक अभी नहीं खुल सका। थोड़ी देर बाद फिर कोशिश करें।',
    expired: 'इस लिंक की अवधि समाप्त हो गई है। भेजने वाले अधिकारी से नया लिंक मांगें।',
    invalid: 'यह लिंक मान्य नहीं है। संदेश जांचें, या भेजने वाले अधिकारी से पूछें।',
    pdfBefore: 'यह आपकी सहमति है, जैसी रिकॉर्ड में दर्ज है।', pdfLink: 'PDF खोलें या डाउनलोड करें',
    pdfAfter: 'और इसे संभालकर रखें। कुछ भी गलत हो तो यह लिंक भेजने वाले अधिकारी को बताएं।',
  },
  bn: {
    title: 'আপনার সম্মতির নথি', opening: 'খুলছে...',
    incomplete: 'এই লিঙ্কটি সম্পূর্ণ নয়। যে বার্তায় এসেছে সেটি দেখুন।',
    failed: 'লিঙ্কটি এখন খোলা গেল না। একটু পরে আবার চেষ্টা করুন।',
    expired: 'এই লিঙ্কের মেয়াদ শেষ হয়েছে। যিনি পাঠিয়েছেন সেই অফিসারের কাছে নতুন লিঙ্ক চান।',
    invalid: 'এই লিঙ্কটি বৈধ নয়। বার্তাটি দেখুন, বা যিনি পাঠিয়েছেন সেই অফিসারকে জিজ্ঞাসা করুন।',
    pdfBefore: 'এটি আপনার সম্মতি, যেমন নথিতে আছে।', pdfLink: 'PDF খুলুন বা ডাউনলোড করুন',
    pdfAfter: 'এবং রেখে দিন। কিছু ভুল থাকলে যিনি এই লিঙ্ক পাঠিয়েছেন সেই অফিসারকে জানান।',
  },
  te: {
    title: 'మీ సమ్మతి రికార్డు', opening: 'తెరుస్తోంది...',
    incomplete: 'ఈ లింక్ పూర్తిగా లేదు. ఇది వచ్చిన సందేశాన్ని సరిచూడండి.',
    failed: 'ఈ లింక్ ఇప్పుడు తెరవబడలేదు. కొద్దిసేపటి తర్వాత మళ్ళీ ప్రయత్నించండి.',
    expired: 'ఈ లింక్ గడువు ముగిసింది. పంపిన అధికారిని కొత్త లింక్ అడగండి.',
    invalid: 'ఈ లింక్ చెల్లదు. సందేశాన్ని సరిచూడండి, లేదా పంపిన అధికారిని అడగండి.',
    pdfBefore: 'ఇది రికార్డులో ఉన్నట్లుగా మీ సమ్మతి.', pdfLink: 'PDF ను తెరవండి లేదా డౌన్‌లోడ్ చేయండి',
    pdfAfter: 'మరియు దాచుకోండి. ఏదైనా తప్పుగా ఉంటే, ఈ లింక్ పంపిన అధికారికి చెప్పండి.',
  },
  ta: {
    title: 'உங்கள் ஒப்புதல் பதிவு', opening: 'திறக்கிறது...',
    incomplete: 'இந்த இணைப்பு முழுமையாக இல்லை. இது வந்த செய்தியைச் சரிபாருங்கள்.',
    failed: 'இந்த இணைப்பை இப்போது திறக்க முடியவில்லை. சிறிது நேரம் கழித்து மீண்டும் முயற்சிக்கவும்.',
    expired: 'இந்த இணைப்பின் காலம் முடிந்துவிட்டது. அனுப்பிய அலுவலரிடம் புதிய இணைப்பைக் கேளுங்கள்.',
    invalid: 'இந்த இணைப்பு செல்லாது. செய்தியைச் சரிபாருங்கள், அல்லது அனுப்பிய அலுவலரிடம் கேளுங்கள்.',
    pdfBefore: 'இது பதிவில் உள்ளபடி உங்கள் ஒப்புதல்.', pdfLink: 'PDF ஐத் திறக்கவும் அல்லது பதிவிறக்கவும்',
    pdfAfter: 'அதைப் பாதுகாத்து வையுங்கள். ஏதேனும் தவறாக இருந்தால், இந்த இணைப்பை அனுப்பிய அலுவலரிடம் சொல்லுங்கள்.',
  },
  kn: {
    title: 'ನಿಮ್ಮ ಸಮ್ಮತಿಯ ದಾಖಲೆ', opening: 'ತೆರೆಯುತ್ತಿದೆ...',
    incomplete: 'ಈ ಲಿಂಕ್ ಪೂರ್ಣವಾಗಿಲ್ಲ. ಇದು ಬಂದ ಸಂದೇಶವನ್ನು ಪರಿಶೀಲಿಸಿ.',
    failed: 'ಈ ಲಿಂಕ್ ಈಗ ತೆರೆಯಲಾಗಲಿಲ್ಲ. ಸ್ವಲ್ಪ ಸಮಯದ ನಂತರ ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ.',
    expired: 'ಈ ಲಿಂಕ್‌ನ ಅವಧಿ ಮುಗಿದಿದೆ. ಕಳುಹಿಸಿದ ಅಧಿಕಾರಿಯಿಂದ ಹೊಸ ಲಿಂಕ್ ಕೇಳಿ.',
    invalid: 'ಈ ಲಿಂಕ್ ಮಾನ್ಯವಲ್ಲ. ಸಂದೇಶವನ್ನು ಪರಿಶೀಲಿಸಿ, ಅಥವಾ ಕಳುಹಿಸಿದ ಅಧಿಕಾರಿಯನ್ನು ಕೇಳಿ.',
    pdfBefore: 'ಇದು ದಾಖಲೆಯಲ್ಲಿರುವಂತೆ ನಿಮ್ಮ ಸಮ್ಮತಿ.', pdfLink: 'PDF ತೆರೆಯಿರಿ ಅಥವಾ ಡೌನ್‌ಲೋಡ್ ಮಾಡಿ',
    pdfAfter: 'ಮತ್ತು ಜೋಪಾನವಾಗಿಡಿ. ಏನಾದರೂ ತಪ್ಪಿದ್ದರೆ, ಈ ಲಿಂಕ್ ಕಳುಹಿಸಿದ ಅಧಿಕಾರಿಗೆ ತಿಳಿಸಿ.',
  },
  gu: {
    title: 'તમારી સંમતિનો રેકોર્ડ', opening: 'ખુલી રહ્યું છે...',
    incomplete: 'આ લિંક પૂરી નથી. જે સંદેશમાં આવી તે તપાસો.',
    failed: 'આ લિંક હમણાં ખૂલી શકી નથી. થોડી વાર પછી ફરી પ્રયાસ કરો.',
    expired: 'આ લિંકની મુદત પૂરી થઈ ગઈ છે. મોકલનાર અધિકારી પાસે નવી લિંક માંગો.',
    invalid: 'આ લિંક માન્ય નથી. સંદેશ તપાસો, અથવા મોકલનાર અધિકારીને પૂછો.',
    pdfBefore: 'આ તમારી સંમતિ છે, જેવી રેકોર્ડમાં છે.', pdfLink: 'PDF ખોલો અથવા ડાઉનલોડ કરો',
    pdfAfter: 'અને સાચવીને રાખો. કંઈ ખોટું હોય તો આ લિંક મોકલનાર અધિકારીને જણાવો.',
  },
};

export function consentLinkWords(language?: string | null): ConsentLinkWords {
  return WORDS[(language ?? '').slice(0, 2).toLowerCase()] ?? WORDS.en!;
}

export const CONSENT_LINK_LANGUAGES = Object.keys(WORDS);
